import { availableDimensions, enumerateValues } from './dimensions';
import {
  applyFilters,
  categoricalValues,
  type CategoricalChip,
  type CategoricalDimension,
  type FilterChip,
} from './filters';
import type { RosterRow } from './rosterRows';

export type FilterSuggestion = {
  dimension: CategoricalDimension;
  dimensionLabel: string;
  value: string;
  valueLabel: string;
};

export const SUGGESTION_LIMIT = 6;

function selectedValues(chips: FilterChip[], dimension: CategoricalDimension): string[] {
  const match = chips.find((chip) => chip.dimension === dimension && 'values' in chip);
  return match != null && 'values' in match ? match.values : [];
}

export function suggestFilters(
  query: string,
  rows: RosterRow[],
  chips: FilterChip[] = [],
  limit: number = SUGGESTION_LIMIT,
  admin = false,
): FilterSuggestion[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];

  const starting: FilterSuggestion[] = [];
  const containing: FilterSuggestion[] = [];

  for (const dimension of availableDimensions({ admin })) {
    if (dimension.kind !== 'categorical') continue;
    const id = dimension.id as CategoricalDimension;
    const already = selectedValues(chips, id);
    const reachable = applyFilters(
      rows,
      chips.filter((chip) => chip.dimension !== id),
    );
    const present = new Set(reachable.flatMap((row) => categoricalValues(row, id)));
    for (const { value, label } of enumerateValues(id, reachable)) {
      if (already.includes(value) || !present.has(value)) continue;
      const haystack = label.toLowerCase();
      const suggestion = {
        dimension: id,
        dimensionLabel: dimension.label,
        value,
        valueLabel: label,
      };
      if (haystack.startsWith(q)) starting.push(suggestion);
      else if (haystack.includes(q)) containing.push(suggestion);
    }
  }

  return [...starting, ...containing].slice(0, limit);
}

export function withSuggestion(
  chips: FilterChip[],
  suggestion: FilterSuggestion,
): FilterChip[] {
  const existing = chips.find(
    (chip) => chip.dimension === suggestion.dimension && 'values' in chip,
  ) as CategoricalChip | undefined;

  if (existing == null) {
    return [...chips, { dimension: suggestion.dimension, values: [suggestion.value] }];
  }
  if (existing.values.includes(suggestion.value)) return chips;

  return chips.map((chip) =>
    chip === existing ? { ...existing, values: [...existing.values, suggestion.value] } : chip,
  );
}
